export const blankWA = {
	storeId: null,
	isLoading: false,
	fromStationId: null,
	toStationId: null,
	departure: null,
	arrival: null,
	crewMember: null,
	status: null,
	version: null,
	id: null,
	stations: [],
	activityType: 'driving',
	isPreparedToBatch: false,
	isBatchedInUI: false,
	isGlobalBatched: false,
	isMultiRide: false,
	run: null,
	runId: null,
	job: null,
	extraActivities: [],
	sequenceGroupId: null,
	rideId: null,
};

const REJECTED_ASSIGNMENT_STATUS = 'rejected';

export const getVisibleExtraActivities = (extraActivities = []) =>
	extraActivities.filter((extraActivity) => extraActivity?.status !== REJECTED_ASSIGNMENT_STATUS);

export const getStationsForAssignment = (fromId, toId, stationList) => {
	const fromIndex = stationList.findIndex((station) => station.id === fromId);
	const toIndex = stationList.findIndex((station) => station.id === toId);

	if (fromIndex === -1 || toIndex === -1 || fromIndex >= toIndex) {
		return [];
	}

	return stationList.slice(fromIndex, toIndex + 1).map((station) => station.id);
};

export const flatOutSequence = (sequence) => {
	const {
		crewMember,
		fromTimestamp,
		toTimestamp,
		status,
		fromStation,
		toStation,
		activityType,
		run,
		job,
		extraActivities,
		sequenceGroupId,
		rideId,
		isMultiRide,
	} = sequence;
	return {
		...blankWA,
		storeId: fromStation.id,
		fromStationId: fromStation.id,
		toStationId: toStation.id,
		status,
		crewMember,
		departure: fromTimestamp,
		arrival: toTimestamp,
		activityType,
		run: run?.name || null,
		runId: run?.id || null,
		job: job?.code || null,
		extraActivities:
			status === REJECTED_ASSIGNMENT_STATUS
				? (extraActivities ?? [])
				: getVisibleExtraActivities(extraActivities),
		sequenceGroupId: sequenceGroupId ?? null,
		rideId: rideId ?? sequence?.ride?.id ?? null,
		isMultiRide: isMultiRide ?? false,
	};
};

export const getMergeStrategyForSequence = (sequence, stations) => {
	const flatSequence = flatOutSequence(sequence);

	const { fromStationId, toStationId } = flatSequence;

	const sequenceStations = getStationsForAssignment(fromStationId, toStationId, stations);

	//get IDs to delete
	const keysToDelete = sequenceStations.slice(0, -1);

	return {
		assignment: { ...flatSequence, stations: sequenceStations },
		keysToDelete,
	};
};

export const generateAssignmentsMapFromSequences = (stationList, sequences) => {
	const assignmentsMap = new Map();

	//fill map with blank assignments
	for (let i = 0; i < stationList.length - 1; i++) {
		assignmentsMap.set(stationList[i].id, {
			...blankWA,
			storeId: stationList[i].id,
			fromStationId: stationList[i].id,
			toStationId: stationList[i + 1].id,
			departure: stationList[i].departure,
			arrival: stationList[i + 1].arrival,
			stations: getStationsForAssignment(stationList[i].id, stationList[i + 1].id, stationList),
		});
	}

	sequences.forEach((sequence) => {
		const { keysToDelete, assignment } = getMergeStrategyForSequence(sequence, stationList);

		keysToDelete.forEach((key) => assignmentsMap.delete(key));
		assignmentsMap.set(assignment.storeId, { ...assignment });
	});

	return assignmentsMap;
};

export const checkIsBatch = (workAssigment, prevAssignment, nextAssigment) => {
	let assignmentToMutate = { ...workAssigment };

	let assignmentKey = assignmentToMutate.fromStationId;
	const keysToDelete = [];

	const isSameAsNext =
		nextAssigment !== undefined &&
		nextAssigment?.status === assignmentToMutate.status &&
		nextAssigment?.crewMember?.id === assignmentToMutate.crewMember.id;

	const isSameAsPrevious =
		prevAssignment !== undefined &&
		prevAssignment?.status === assignmentToMutate.status &&
		prevAssignment?.crewMember?.id === assignmentToMutate.crewMember.id;

	if (isSameAsNext) {
		const {
			toStationId: nextToStationId,
			fromStationId: nextFromStationId,
			arrival: nextArrival,
		} = nextAssigment;

		assignmentToMutate = {
			...assignmentToMutate,
			toStationId: nextToStationId,
			arrival: nextArrival,
		};
		keysToDelete.push(nextFromStationId);
	}

	if (isSameAsPrevious) {
		const { fromStationId: prevFromStationId, departure: prevDeparture } = prevAssignment;
		const currentStoreKey = assignmentToMutate.fromStationId;
		assignmentToMutate = {
			...assignmentToMutate,
			fromStationId: prevFromStationId,
			departure: prevDeparture,
		};
		assignmentKey = prevFromStationId;
		keysToDelete.push(currentStoreKey);
	}
	const result = {
		assignment: { ...assignmentToMutate, storeId: assignmentKey },
		storeKey: assignmentKey,
		toDelete: keysToDelete,
	};
	return result;
};

export const breakFullBatch = (batchedAssignment, allStations) => {
	const stationsBatched = getStationsForAssignment(
		batchedAssignment.fromStationId,
		batchedAssignment.toStationId,
		allStations
	);
	const emptyAssignments = stationsBatched.reduce((acc, currentStation, index) => {
		if (stationsBatched[index + 1] === undefined) {
			return acc;
		}
		acc.push({
			...blankWA,
			storeId: currentStation,
			fromStationId: currentStation,
			toStationId: stationsBatched[index + 1],
			stations: [stationsBatched[index], stationsBatched[index + 1]],
		});
		return acc;
	}, []);
	return emptyAssignments;
};
